import type { WeightData } from "~/domains/product/types/WeightData";
import { type ExternalBrand, type ExternalBrandId, type ImageDataDTO } from "~/domains/product/types/ProductDataDTO";
import type { FeatureData, ImgData } from "~/utils/models";
import type { TechnicalDetailsData } from "~/features/productDetails/dto/PdpPageProductDataDto";
import type { ToolType } from "~/domains/tools/types/ToolType";
import type { SalesStatus } from "~/features/productDetails/components/PhaseInOut/SalesStatus";
import type { SalesCampaignProduct } from "~/domains/salesCampaign/types/SalesCampaign";
import type { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import type { TranslationModel } from "~/common/types/TranslationModel";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

export type ProductData = PlainProductData | ConfiguratorProductData | KITProductData;

export interface BaseProductData {
  /** Code is a unique key */
  code: string;
  /**
   * Code to use when sending data to hybris.
   * Normally it's the same as code, but it will be different in case of Configurator(TBD) products
   */
  hybrisCode: string;
  /**
   * Code to use when requesting PnA data
   * Normally it's the same as code, but it will be different for UNIVERSAL housing product
   */
  pnaCode: string;
  // True when data is coming from an endpoint which provides full product data - e.g. PDP or loadProductData api
  isFullProduct?: boolean;
  type: ProductType;
  preferred?: boolean;

  manufacturerLogoUrl?: string;
  manufacturerBrandId?: ExternalBrandId;
  brandLogoUrl?: string;
  images: ImageDataDTO[];
  url: string;

  /** Designation may not be used as a unique key! It can repeat */
  designation: string;
  marketDesignation: string;
  displayMMDAssistant: boolean;
  cmnSearchMatched: boolean;
  sapMaterialNumber: string;
  shortDescription: TranslationModel;
  description: string;
  baseType: string;
  /** Collection of 13 digit level customer material numbers */
  aliases: string[];
  /** Collection of 15 digit level customer material numbers */
  packageAliases: Dictionary<string[]>;
  weight?: WeightData;
  weightImperial?: WeightData;
  productLineId: string;
  productAllCategories: string[];
  oldDesignation?: string;
  isOptime: boolean;
  quantityVisible: boolean;
  /** Do not use directly, use productDescriptionUtils#isPMA */
  isPMA: boolean;
  /** Do not use directly, use productDescriptionUtils#isPMA */
  marketType: ProductMarketType | null;

  fifteenDigit: FifteenDigitData;
  gpsdGroup: string | null;
  successorProduct?: ProductData;
  alternativeProducts: ProductData[];

  features: FeatureData[];
  primaryImage?: ImgData;

  technicalDetails: TechnicalDetailsData[];

  numberOfBolts: string;

  salesCampaign: SalesCampaignProduct;

  configuratorTypes: ToolType[];

  customerRequestedCADAvailability?: boolean;

  showPostCartModal?: boolean;
  externalBrand?: ExternalBrand;
  configuratorUrl?: string;
  lmbtConfigurator?: string;

  /*
   * Means that the product is not in Schaeffler's ERP and cannot be normally ordered
   *
   * Remove:
   * - qty input
   * - add to wishlist
   * - normal add to cart
   */
  externalProduct?: boolean;
  reconditionable?: boolean;
  additionalCalculationUrl?: string;
  isProductBackInStock?: boolean;
  stockAmount?: number;
}

export enum ProductMarketType {
  DMA = "DMA",
  PMA = "PMA",
}

export interface PublicationDownloadFile {
  url: string;
  title: string;
  pubid: string;
  description: string;
  href: string;
  size: string;
  editor: string;
  filetype: string;
  format: string;
  isdownloadable: boolean;
  isorderable: boolean;
  iswebshop: boolean;
  language: string;
  languageCode: string;
  languageKey: string;
  mediaCategoryCode: string;
  mediaCategory: string;
  mediaType: string;
  orderNumber: string;
  orderNumberSet: boolean;
  pages: string;
  image: string;
  shortId: string;
  shortIdSet: boolean;
  shortTitle: string;
  shortTitleRaw: string;
  shortTitleVisible: boolean;
  subline: string;
  titleRaw: string;
  titleShow: boolean;
  year: string;
}

export interface PDPdownloadPdfFile {
  lang: string;
  values: PublicationDownloadFile[];
}

export enum ProductType {
  DEFAULT = "base",
  CONFIGURATOR = "configurator",
  KIT = "kit",
}

export interface PlainProductData extends BaseProductData {
  type: ProductType.DEFAULT;
}

export interface ConfiguratorProductData extends BaseProductData {
  type: ProductType.CONFIGURATOR;
  configuratorType: ConfiguratorType;
  subtype: "unit" | "part";
  leadTime: number;
  guideway: string;
  carriages: string;
  configId: string;
}

export interface KITProductData extends BaseProductData {
  type: ProductType.KIT;

  housingType: HousingType;
  optionalParts: HousingProduct[];
  includedParts: HousingProduct[];
}

export interface PackagingInformationEntry {
  packSize: number;
  /**
   * Guaranteed to be 2 digits
   */
  packCode: string;

  preferredPackQuantity: number | null;
  replenishmentLeadTimeDate: Date | null;

  /**
   * @deprecated
   * Required to perform add to cart...
   * use totalWarehouse.code instead
   */
  primaryWarehouseCode: string;
  /**
   * @deprecated
   * Required to perform add to cart...
   * use totalWarehouse.code instead
   */
  primaryWarehouseName: string;

  /**
   * @deprecated
   * https://agile.deloitte.de/browse/SCHAEFF-28477
   */
  global: WarehouseStockData;
  /**
   * @deprecated
   * https://agile.deloitte.de/browse/SCHAEFF-28477
   */
  warehouses: WarehouseStockData[];
  /**
   * New, added by
   * https://agile.deloitte.de/browse/SCHAEFF-28477
   */
  totalWarehouse: WarehouseStockData;
  salesStock?: Record<string, number>;
  minOrderQuantity: number;
}

export interface WarehouseStockData {
  code: string;
  name: string;
  inStock: InStockEntryData[];
  inProduction: InProductionEntryData[];
  inClarification: number;
  inStockWeekly: InStockOrProductionEntryDataInCW[];
  inProductionWeekly: InStockOrProductionEntryDataInCW[];
}

export interface InProductionEntryData {
  date: Date;
  quantity: number;
}
export interface InStockEntryData {
  date?: Date;
  quantity: number;
}

export interface InStockOrProductionEntryDataInCW {
  cwDate: string;
  date?: Date;
  quantity: number;
}

export interface FifteenDigitData {
  [packCode: string]: FifteenDigitEntry;
}

export interface FifteenDigitEntry {
  packCode: string;
  countryOfOrigin?: string;
  materialNo: string;
  schaefflerEan: string;
  customsData: string[];

  packSize: number;

  salesStatus: SalesStatus;
  isFcc?: boolean;
  fccAvailabilityMonths?: number;
  rlt?: number;
  preselect?: boolean;
  fccBreadcrumb?: {
    code: string;
    name: string;
  }[];
}

export interface HousingProduct {
  id: string;
  product: ProductData;
  designation: string;
  quantity: number;
  type: HousingProductType;
  variantCode: string;
}

export enum HousingProductType {
  HOUSING = "HOUSING",
  BEARING = "BEARING",
  ADAPTER_SLEEVE = "ADAPTER_SLEEVE",
  LOCATING_RING = "LOCATING_RING",
  SEAL = "SEAL",
  END_COVER = "END_COVER",
  LOCKNUT = "LOCKNUT",
  WASHER = "WASHER",
  NOT_HOUSING_PRODUCT_TYPE = "NOT_HOUSING_PRODUCT_TYPE",
}

export enum HousingType {
  PRODUCT_LINE_72 = "PRODUCT_LINE_72",
  KIT = "KIT",
  UNIVERSAL = "UNIVERSAL",
}

export function getProductData(product: undefined | null): null;
export function getProductData(product: ProductDescriptor | ProductData): ProductData;
export function getProductData(product: ProductDescriptor | ProductData | null | undefined): ProductData | null;

export function getProductData(product: ProductDescriptor | ProductData | null | undefined): ProductData | null {
  if (!product) {
    return null;
  }
  return "base" in product ? product.base : product;
}

export function isKITProduct(product?: ProductData | null): product is KITProductData;
export function isKITProduct(product?: ProductDescriptor | null): product is ProductDescriptor<KITProductData>;
export function isKITProduct(product?: ProductDescriptor | ProductData | null) {
  const data = getProductData(product);
  return data?.type === ProductType.KIT;
}

export function isKITKITProduct(product?: ProductData | null): product is KITProductData;
export function isKITKITProduct(product?: ProductDescriptor | null): product is ProductDescriptor<KITProductData>;

/**
 * KIT is a collection of housing products that contains ONLY optional parts
 * @param product
 */
export function isKITKITProduct(product?: ProductData | ProductDescriptor | null) {
  const data = getProductData(product);
  return isKITProduct(data) && data.housingType === HousingType.KIT;
}

export function isUniversalKITProduct(product?: ProductData | null): product is KITProductData;
export function isUniversalKITProduct(product?: ProductDescriptor | null): product is ProductDescriptor<KITProductData>;

/**
 * Universal kit is a collection of housing products that contains BOTH optional and included parts
 * @param product
 */
export function isUniversalKITProduct(product?: ProductDescriptor | ProductData | null): product is KITProductData {
  const data = getProductData(product);
  return isKITProduct(data) && data.housingType === HousingType.UNIVERSAL;
}

export function isConfiguratorProduct(product?: ProductData | null): product is ConfiguratorProductData;
export function isConfiguratorProduct(
  product?: ProductDescriptor | null,
): product is ProductDescriptor<ConfiguratorProductData>;
export function isConfiguratorProduct(product?: ProductDescriptor | ProductData | null) {
  return getProductData(product)?.type === ProductType.CONFIGURATOR;
}

export function isLinearUnit(product?: ProductData | null): product is ConfiguratorProductData;
export function isLinearUnit(product?: ProductDescriptor | null): product is ProductDescriptor<ConfiguratorProductData>;
export function isLinearUnit(product?: ProductDescriptor | ProductData | null) {
  const data = getProductData(product);
  return isConfiguratorProduct(data) && data.subtype === "unit";
}
